import {
  tenantMembersControllerInviteMember,
  tenantMembersControllerListMembers,
  tenantMembersControllerRemoveMember,
  userControllerUpdateSelf,
} from '@/generated/korza-api-client';
import type { MemberDto } from '@/generated/korza-api-client/types.gen';
import { useTenant } from '@/hooks/use-tenant';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export function tenantMembersQueryKey(tenantSlug: string) {
  return ['tenant-members', tenantSlug] as const;
}

// Failures the screen reacts to specifically; anything else is 'unknown'.
export type TeamMemberErrorKind =
  | 'invalid'
  | 'forbidden'
  | 'not-found'
  | 'last-member'
  | 'rate-limited'
  | 'unknown';

export class TeamMemberRequestError extends Error {
  constructor(readonly kind: TeamMemberErrorKind) {
    super(`Team member request failed: ${kind}`);
  }
}

function errorKindFromStatus(status: number): TeamMemberErrorKind {
  switch (status) {
    case 400:
      return 'invalid';
    case 403:
      return 'forbidden';
    case 404:
      return 'not-found';
    case 409:
      return 'last-member';
    case 429:
      return 'rate-limited';
    default:
      return 'unknown';
  }
}

export function useTenantMembers() {
  const tenant = useTenant();

  return useQuery<MemberDto[], TeamMemberRequestError>({
    queryKey: tenantMembersQueryKey(tenant.slug),
    queryFn: async () => {
      const result = await tenantMembersControllerListMembers({
        path: { tenantSlug: tenant.slug },
      });

      if (result.error !== undefined || !result.data) {
        throw new TeamMemberRequestError(
          errorKindFromStatus(result.response.status)
        );
      }

      return result.data;
    },
  });
}

export function useInviteMember() {
  const tenant = useTenant();
  const queryClient = useQueryClient();

  return useMutation<void, TeamMemberRequestError, { email: string }>({
    mutationFn: async ({ email }) => {
      const result = await tenantMembersControllerInviteMember({
        path: { tenantSlug: tenant.slug },
        body: { email },
      });

      if (result.error !== undefined) {
        throw new TeamMemberRequestError(
          errorKindFromStatus(result.response.status)
        );
      }
    },
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: tenantMembersQueryKey(tenant.slug),
      }),
  });
}

export function useRemoveMember() {
  const tenant = useTenant();
  const queryClient = useQueryClient();

  return useMutation<void, TeamMemberRequestError, { adminUserId: string }>({
    mutationFn: async ({ adminUserId }) => {
      const result = await tenantMembersControllerRemoveMember({
        path: { tenantSlug: tenant.slug, adminUserId },
      });

      if (result.error !== undefined) {
        throw new TeamMemberRequestError(
          errorKindFromStatus(result.response.status)
        );
      }
    },
    // Also on failure: a 409/404 means the list on screen is stale.
    onSettled: () =>
      queryClient.invalidateQueries({
        queryKey: tenantMembersQueryKey(tenant.slug),
      }),
  });
}

export function useUpdateOwnName() {
  const tenant = useTenant();
  const queryClient = useQueryClient();

  return useMutation<void, TeamMemberRequestError, { name: string }>({
    mutationFn: async ({ name }) => {
      const result = await userControllerUpdateSelf({ body: { name } });

      if (result.error !== undefined) {
        throw new TeamMemberRequestError(
          errorKindFromStatus(result.response.status)
        );
      }
    },
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: tenantMembersQueryKey(tenant.slug),
      }),
  });
}
